import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import clone from 'ramda/src/clone';
import findIndex from 'ramda/src/findIndex';
import isEmpty from 'ramda/src/isEmpty';
import length from 'ramda/src/length';
import move from 'ramda/src/move';
import remove from 'ramda/src/remove';
import pickBy from 'ramda/src/pickBy';
import propEq from 'ramda/src/propEq';
import Button from '@components/@core/AnimatedButton';
import Dropdown from '@components/@core/Dropdown';
import Filter from '@components/@core/Filter';
import Icon from '@components/@core/Icon';
import Input from '@components/@core/Input';
import Thumbnail from '@components/@core/Thumbnail';
import Settings from '@components/@core/Settings';
import AppActions from '~/actions/app';
import Module from './Module';
import { isNotNull } from '~/services/util';

const ADDMODULES = [
    { id: 0, value: 'Add Existing', name: 'old' },
    { id: 1, value: 'Create New', name: 'new' },
];

const Add = ({ onUpdate }) => {
    const [nameEn, setNameEn] = useState('');
    const [descriptionEn, setDescriptionEn] = useState('');
    const [nameEs, setNameEs] = useState('');
    const [descriptionEs, setDescriptionEs] = useState('');
    const [thumbnail, setThumbnail] = useState('');
    const [children, setChildren] = useState([]);
    const [objectives, setObjectives] = useState(['', '']);
    const [selected, setSelected] = useState({
        author: [],
        department: [],
        competency: [],
        category: [],
    });
    const [chronologicalLock, setChronologicalLock] = useState(false);
    const [timeLock, setTimeLock] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const handleObjectiveChange = (index) => (e) => {
        const _objectives = clone(objectives);
        _objectives[index] = e;
        setObjectives(_objectives);
    };
    const handleObjectiveAdd = () => {
        const _objectives = clone(objectives);
        if (_objectives.length <= '4') {
            _objectives.push('');
            setObjectives(_objectives);
        }
    };
    const handleObjectiveDelete = (index) => () => {
        const _objectives = clone(objectives);
        _objectives.splice(index, 1);
        setObjectives(_objectives);
    };
    const handleSelectTags = (key) => (tags) => {
        const _selected = clone(selected);
        _selected[key] = tags;
        setSelected(_selected);
    };
    const handleSubmit = (published) => () => {
        if (!nameEn || !descriptionEn || !children || children.length === 0 || !selected.author) return;

        const modules = children.map((item) => ({
            blocked_by: item.blocked_by,
            card_template_id: item.id,
            card_type_id: item.card_type_id,
            delay_days: item.data.delay_days,
        }));
        const payload = {
            template: {
                card_type_id: 1,
                author_id: selected.author[0],
                access_type: 1,
                published,
                data: {
                    en: { name: nameEn, description: descriptionEn },
                    es: { name: nameEs, description: descriptionEs },
                    category: selected.category,
                    competency: selected.competency,
                    department: selected.department,
                    objectives,
                    thumb_url: thumbnail,
                    attachment_hash: '',
                    attachments: [],
                    estimated_completion: '',
                    action_approval: 'self',
                    require_attachments: '',
                    assessment_id: null,
                    presentation_url: '',
                    link: '',
                    study_content: '',
                    video_url: '',
                    embed_url: '',
                    modules,
                    chronological_lock: chronologicalLock,
                    time_lock: timeLock,
                },
            },
        };
        onUpdate(payload, [thumbnail]);
    };
    const handleToggleModal = (e) => {
        if (e[0].name === 'old') {
            dispatch(
                AppActions.modalRequest({
                    type: 'Attach Library',
                    data: { before: { modules: [], show: ['modules'], selected: children } },
                    callBack: { onAttach: (e) => setChildren(e.templates) },
                })
            );
        } else {
            dispatch(AppActions.modalRequest({ type: 'Add New Module', data: { after: { modules: children } } }));
        }
    };
    const handleChildMenu = (event, item) => {
        const index = findIndex(propEq('id', item.id), children);
        const options = queryString.stringify(pickBy(isNotNull, { filter: null, page: 1, perPage: 25 }));
        if (index > -1) {
            let newChildren = clone(children);
            if (event === 'move up') {
                if (index === 0) return;
                newChildren = move(index, index - 1, newChildren);
            } else if (event === 'move down') {
                if (newChildren?.length === index + 1) return;
                newChildren = move(index, index + 1, newChildren);
            } else if (event === 'remove') {
                newChildren = remove(index, 1, newChildren);
            } else if (event === 'preview view') {
                dispatch(
                    AppActions.modalRequest({
                        type: 'Preview',
                        data: { before: { course: { children: [item] }, module: item, index: 0 } },
                    })
                );
            } else if (event === 'detail view') {
                navigate(`/library/training/modules/${item.id}/view?${options}`);
            }
            setChildren(newChildren);
        }
    };

    const role = useSelector((state) => state.app.app_role_id);
    const authors = useSelector((state) => state.app.authors);
    const departments = useSelector((state) => state.app.departments);
    const competencies = useSelector((state) => state.app.competencies);
    const categories = useSelector((state) => state.app.categories);
    const modalData = useSelector((state) => state.app.modalData);
    const disabled = !nameEn || !descriptionEn || !children || children.length === 0 || isEmpty(selected.author);
    const disableDraft = !nameEn || !descriptionEn || isEmpty(selected.author);

    useEffect(() => {
        if (modalData?.after?.modules) {
            setChildren(modalData.after.modules);
        }
    }, [modalData]);

    const handleSelectMenu = () => (event, module) => {
        const quotaLen = length(children);
        let modules = clone(children);
        const indx = findIndex(propEq('id', module.id), modules);
        if (indx < 0) return;

        switch (event) {
            case 'move up':
                if (indx !== 0) modules = move(indx, indx - 1, modules);
                break;
            case 'move down':
                if (quotaLen !== indx + 1) modules = move(indx, indx + 1, modules);
                break;
            case 'remove':
                modules = remove(indx, 1, modules);
                break;
        }
        setChildren(modules);
    };

    const settings = [
        { name: 'Author', id: null, options: authors, select: 'author' },
        { name: 'Departments', id: null, options: departments, select: 'department' },
        { name: 'Competencies', id: null, options: competencies, select: 'competency' },
        { name: 'Categories', id: null, options: categories, select: 'category' },
    ];

    return (
        <>
            <Filter back="all courses" />
            <div className="library-course-card border-5">
                <div className="detail-content">
                    <div className="d-h-between mb-3">
                        <span className="dsl-b22 bold">Course</span>
                    </div>
                    <div className="d-flex jc-between ssm-column">
                        <div className="ssm-100 w-50 w-sm-100 mb-3">
                            <div className="pl-1 mb-3">
                                <Input className="course-input mb-3" title="Name" dataCy="title" value={nameEn} maxLength={50} placeholder="Type here..." onChange={(e) => setNameEn(e)} />
                            </div>
                            <div className="pl-1 mb-3">
                                <Input
                                    className="course-text"
                                    title="Description"
                                    dataCy="description"
                                    placeholder="Type here..."
                                    as="textarea"
                                    rows="3"
                                    maxLength={150}
                                    value={descriptionEn}
                                    onChange={(e) => setDescriptionEn(e)}
                                />
                            </div>
                            <div className="pl-1 mb-3">
                                <Input className="course-input mb-3" title="Name (Spanish)" dataCy="title-es" value={nameEs} maxLength={50} placeholder="Type here..." onChange={(e) => setNameEs(e)} />
                            </div>
                            <div className="pl-1 mb-3">
                                <Input
                                    className="course-text"
                                    title="Description (Spanish)"
                                    dataCy="description-es"
                                    placeholder="Type here..."
                                    as="textarea"
                                    rows="3"
                                    maxLength={150}
                                    value={descriptionEs}
                                    onChange={(e) => setDescriptionEs(e)}
                                />
                            </div>
                            <div className="d-flex ai-start ps-1">
                                <p className="career-text core-input-label dsl-b12 pt-2 m-0">Learning Objectives</p>
                                <div className="d-flex flex-column w-100">
                                    {objectives.map((item, index) => (
                                        <Input
                                            className="course-input mb-2 d-flex-1"
                                            value={objectives[index]}
                                            placeholder="Type here..."
                                            direction="vertical"
                                            onChange={handleObjectiveChange(index)}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="ssm-100 w-40 w-sm-100">
                            <Thumbnail type="upload" isDisablePicker source={thumbnail} size="medium" label="Upload image" onDrop={(e) => setThumbnail(e)} onDelete={() => setThumbnail(null)} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="library-course-card mt-2 border-5">
                <div className="modules-content pb-0">
                    <div className="d-flex">
                        <p className="dsl-b22 bold">Modules Attached</p>
                    </div>
                    <Module data={children} role={role} onMenu={handleChildMenu} onSelect={handleSelectMenu()} edit />
                    <div className="d-h-start justify-content-end my-3 my-sm-0 pb-2 pt-4">
                        <Button className="ml-auto" type="link">
                            <Icon name="fas fa-plus" size={14} color="#376caf" />
                            <Dropdown
                                className="pl-1"
                                align="right"
                                width="fit-content"
                                caret="none"
                                returnBy="data"
                                placeholder="Attach Module"
                                selectable={false}
                                data={ADDMODULES}
                                onChange={handleToggleModal}
                            />
                        </Button>
                    </div>
                </div>
            </div>
            <Settings isEditable expanded values={settings} handleSelect={handleSelectTags} />
            <div className="mt-3 d-flex jc-end">
                <Button className="mr-3" name="Draft" disabled={disableDraft} type="low" onClick={handleSubmit(0)} />
                <Button name="Publish" disabled={disabled} onClick={handleSubmit(1)} />
            </div>
        </>
    );
};

export default Add;
